import { useAuth } from "../auth.js";
import type { LessonCreditSummary } from "../booking/api.js";
import { OfficeTutorBookingPanel } from "../booking/OfficeTutorBookingPanel.js";
import { useStudentCreditReads } from "../booking/StudentCreditReads.js";
import { TutorBookLesson } from "../booking/TutorBookLesson.js";
import { useAsync } from "../useAsync.js";

export function studentRecordCreditSummary(
  summary: LessonCreditSummary | null,
  studentId: string,
): LessonCreditSummary | null {
  return summary?.portal_student_id === studentId ? summary : null;
}

/**
 * The student-record booking task, separated from general Admin tools.
 *
 * Office/owner users keep the existing explicit tutor chooser. Ordinary tutors
 * reuse the same authoritative credit read model and canonical booking component
 * as TutorStudentTools, locked to their own booking-provider identity.
 */
export function StudentRecordBooking({
  studentId,
  studentName,
  canManageOtherTutors,
  onChanged,
}: {
  studentId: string;
  studentName: string;
  canManageOtherTutors: boolean;
  onChanged: () => void;
}) {
  const { me } = useAuth();
  const creditReads = useStudentCreditReads();
  const ownProviderId = me?.booking.providerId ?? null;
  const credits = useAsync(
    () =>
      canManageOtherTutors
        ? Promise.resolve(null)
        : creditReads.lessonCredits(studentId),
    [studentId, canManageOtherTutors, creditReads],
  );

  if (canManageOtherTutors) {
    return (
      <OfficeTutorBookingPanel
        studentId={studentId}
        studentName={studentName}
        onChanged={onChanged}
      />
    );
  }

  if (ownProviderId === null) {
    return (
      <div className="panel">
        <p className="empty">Your tutor account is not linked to a booking provider.</p>
      </div>
    );
  }

  if (credits.loading && !credits.data) {
    return (
      <div className="panel">
        <p className="empty">Loading booking options…</p>
      </div>
    );
  }

  if (credits.error) {
    return <div className="error">We could not load this student&apos;s booking options.</div>;
  }

  const creditData = studentRecordCreditSummary(credits.data, studentId);
  if (credits.data && !creditData) {
    return <div className="error">The booking options did not match the selected student.</div>;
  }

  if (!creditData) return null;

  return (
    <TutorBookLesson
      studentId={studentId}
      studentName={studentName}
      blocks={creditData.blocks}
      selectedTutorProviderId={ownProviderId}
      onBooked={() => {
        creditReads.invalidate();
        credits.reload();
        onChanged();
      }}
    />
  );
}
